import { RouteMeta, RouteRecordRaw } from 'vue-router';
import { storeToRefs } from 'pinia';
import { usePropertyStore } from '@/common/stores/property';
import {
  checkAllPermissionDenied,
  findPermissionByTranslateId,
} from '@/common/permission/permission.utils';
import { useViewModeStore } from '@/common/stores/view-mode';

import { VIEW_MODE } from '@/common/define/common.define';
import { i18n } from '@/common/locale';
import type { SelectboxItem } from '@/common/utils/types';
import {
  type Role,
  type RoleId,
  RoleIdMap,
  roleHierarchyWeightByRoleId,
  Supervisors,
} from './auth.defines';

export const convertToRoleId = (role: Role) => {
  return RoleIdMap[role];
};

export const isRoleIdIncluded = (allowedRoles: Role[], roleId: RoleId) => {
  return allowedRoles.map(convertToRoleId).includes(roleId);
};

export const checkRole = {
  isAdmin: (roleId?: RoleId): boolean => roleId === RoleIdMap.Admin,
  isMaster: (roleId?: RoleId): boolean => roleId === RoleIdMap.Master,
  isUser: (roleId?: RoleId): boolean => roleId === RoleIdMap.User,
  isSupervisor: (roleId?: RoleId): boolean => !!roleId && isRoleIdIncluded(Supervisors, roleId),
};

const USER_ROLE_LEVEL = roleHierarchyWeightByRoleId[RoleIdMap.User];

/** 없거나 정의에 없는 역할은 최하위 User 로 본다 */
const getRoleLevel = (roleId?: RoleId): number =>
  roleHierarchyWeightByRoleId[roleId ?? RoleIdMap.User] ?? USER_ROLE_LEVEL;

/**
 * Master 는 대상 역할을 보지 않고 전부 허용(Master↔Master 포함), Admin 은 나보다 낮은 역할만 허용한다.
 */
export const canEditUser = (myRoleId?: RoleId, targetRoleId?: RoleId): boolean => {
  if (checkRole.isMaster(myRoleId)) {
    return true;
  }
  // 내가 Admin 이 아니면(User 이거나 미상이면) 아무도 수정할 수 없다
  if (!checkRole.isAdmin(myRoleId)) {
    return false;
  }
  return getRoleLevel(targetRoleId) < getRoleLevel(myRoleId);
};

/** Master / Admin / User 셀렉트·필터 등 공통 (userWindow ROLE_ITEMS와 동일 데이터) */
export const getRoleSelectboxItems = (): SelectboxItem<RoleId>[] => [
  { name: i18n.global.t('WORD.MASTER'), value: RoleIdMap.Master },
  { name: i18n.global.t('WORD.ADMIN'), value: RoleIdMap.Admin },
  { name: i18n.global.t('WORD.USER'), value: RoleIdMap.User },
];

export const getDisplayRoleName = (roleId?: RoleId): string => {
  if (checkRole.isMaster(roleId)) return i18n.global.t('WORD.MASTER');
  if (checkRole.isAdmin(roleId)) return i18n.global.t('WORD.ADMIN');
  return i18n.global.t('WORD.USER');
};
export const getDisplayRoleType = (roleId?: RoleId): 'master' | 'admin' | 'user' => {
  if (checkRole.isMaster(roleId)) return 'master';
  if (checkRole.isAdmin(roleId)) return 'admin';
  return 'user';
};

export const extractAccessibleRoutes = (
  routes: RouteRecordRaw[],
  roleId?: RoleId,
): RouteRecordRaw[] => {
  const { isMaxgaugeMode } = useViewModeStore();
  const propertyStore = usePropertyStore();
  const { isEsLogEnabled } = storeToRefs(propertyStore);
  const filteredRoutes: RouteRecordRaw[] = [];

  // 뷰모드에 따른 메뉴 display 체크
  const shouldDisplayViewMode = (meta: RouteMeta | undefined) =>
    !isMaxgaugeMode || (isMaxgaugeMode && !meta?.invisibleViewMode?.includes(VIEW_MODE.MAXGAUGE));

  const hasAccess = (route: RouteRecordRaw) => {
    const roles = route.meta?.roles?.map(convertToRoleId);
    const permission = findPermissionByTranslateId(route.meta?.rolePermission);
    const isMaster = checkRole.isMaster(roleId);
    const isAllChildPermissionDenied =
      !isMaster && checkAllPermissionDenied(route.meta?.childRolePermission);

    const roleCheck = !roles || (roleId != null && roles.includes(roleId));
    const permissionCheck =
      isMaster || permission == null || permission.menuOnOff || permission.listOnOff;
    const esLogCheck = !route.meta?.requiresESLog || isEsLogEnabled.value;

    return roleCheck && permissionCheck && !isAllChildPermissionDenied && esLogCheck;
  };

  routes.forEach((route: RouteRecordRaw) => {
    // 라우트의 meta.roles의 roleId가 유저의 roleId를 포함하는지 확인
    if (hasAccess(route)) {
      const { children, meta, ...restParams } = route;
      const isDisplayMenu = shouldDisplayViewMode(meta);
      // 라우트에 children이 있는 경우 재귀적으로 함수 호출하여 확인
      if (children && children.length > 0) {
        const childAdminRoutes = extractAccessibleRoutes(children, roleId);
        // 추출된 하위 라우트가 있는 경우 filteredRoutes에 추가
        if (childAdminRoutes.length > 0 && isDisplayMenu) {
          filteredRoutes.push({
            ...restParams,
            meta,
            children: childAdminRoutes,
          });
        }
      } else if (isDisplayMenu) {
        filteredRoutes.push({ ...restParams, meta, children: [] });
      }
    }
  });

  return filteredRoutes;
};
